import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { PageLayout } from '@/layouts'
import CPFInput, { validaCPF } from '@/components/form/input/cpf-mask-input'
import { CNPJInput, WhatsAppInput } from '@/components/form/input'
import { CEPInput } from '@/components/form/input/cep-mask-input'
import PasswordConfirmationFields from '@/components/form/input/password-confirmation-fields'
import { FeedbackDialog } from '@/components/form/feedback-dialog'
import { useCreateUsuario } from '@/features/users/services'
import { useCreateAgreementUser } from '@/features/agreement/services/useCreateAgreementUser'
import { useCurrentUser } from '@/features/auth/state/auth'
import { useUtils } from '@/lib/hooks/utils'
import { numeroPorExtenso } from '@/utils/numeroPorExtenso'
import { isValidPersonName, RemovaCharEspeciais } from '@/utils/format'
import { getCleanDomain } from '@/utils/getCleanDomain'
import { TypeContratoServico } from '@/types/contrato-servico'
import { TypeCargoUser } from '@/lib/enums'

const passwordSchema = z
  .string()
  .min(6, 'A senha deve conter pelo menos 6 caracteres')
  .refine(value => /[A-Z]/.test(value), {
    message: 'A senha deve conter pelo menos uma letra maiúscula'
  })
  .refine(value => /[!@#$%^&*(),.?":{}|<>]/.test(value), {
    message: 'A senha deve conter pelo menos um caractere especial'
  })
  .refine(value => /\d/.test(value), {
    message: 'A senha deve conter pelo menos um número'
  })

const formSchema = z
  .object({
    nome: z.string().min(1, 'Nome é obrigatório').refine(isValidPersonName, 'Nome não pode conter números'),
    cpf: z.string().refine(val => validaCPF(val), { message: 'CPF inválido' }),
    email: z.string().email('Email inválido'),
    registerCompany: z.enum(['yes', 'no'], {
      errorMap: () => ({ message: 'Escolha se deseja cadastrar a empresa' })
    }),
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Confirme a senha'),
    whatsApp: z.string().min(10, 'WhatsApp deve ter ao menos 10 dígitos'),
    tel: z.string().optional(),
    cargo: z.string().min(1, 'Cargo é obrigatório'),
    comissao: z
      .string()
      .min(1, 'Comissão é obrigatória')
      .refine(value => !Number.isNaN(Number(value)) && Number(value) >= 0 && Number(value) <= 100, {
        message: 'O valor deve estar entre 0 e 100'
      }),
    comissao_escrita: z.string().optional(),
    cep: z.string().optional(),
    logradouro: z.string().optional(),
    bairro: z.string().optional(),
    numero: z.string().optional(),
    municipio: z.string().optional(),
    estado: z.string().optional(),
    complemento: z.string().optional(),
    cnpj: z.string().optional(),
    razao_social: z.string().optional(),
    cnaeCode: z.string().optional(),
    cnaeText: z.string().optional()
  })
  .superRefine((data, ctx) => {
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'As senhas digitadas não são idênticas.',
        path: ['confirmPassword']
      })
    }

    ;(['cep', 'logradouro', 'bairro', 'numero', 'municipio', 'estado'] as const).forEach(field => {
      if (!data[field]?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Campo obrigatório',
          path: [field]
        })
      }
    })

    if (data.registerCompany === 'yes') {
      ;(['cnpj', 'razao_social', 'cnaeCode', 'cnaeText'] as const).forEach(field => {
        if (!data[field]?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Campo obrigatório',
            path: [field]
          })
        }
      })

      if (RemovaCharEspeciais(data.cnpj || '').length !== 14) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CNPJ inválido',
          path: ['cnpj']
        })
      }
    }
  })

type FormValues = z.infer<typeof formSchema>

const ROLE_OPTIONS = [
  { value: TypeCargoUser.PARCEIRO, label: 'Parceiro Comercial' },
  { value: TypeCargoUser.GERENTE, label: 'Parceiro Gestor' }
] as const

function getAvailableRoles(currentCargo?: string) {
  if (currentCargo === TypeCargoUser.CEO || currentCargo === TypeCargoUser.GERENTE) {
    return ROLE_OPTIONS
  }

  return ROLE_OPTIONS.filter(option => option.value === TypeCargoUser.PARCEIRO)
}

export default function CreateUsuario() {
  const { mutate: createUsuario } = useCreateUsuario()
  const { mutate: createAgreement } = useCreateAgreementUser()
  const { buscarCNPJ, buscarCEP } = useUtils()
  const user = useCurrentUser()
  const domain = getCleanDomain()

  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isCepLoading, setIsCepLoading] = useState(false)
  const [isCnpjLoading, setIsCnpjLoading] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nome: '',
      cpf: '',
      email: '',
      registerCompany: undefined,
      password: '',
      confirmPassword: '',
      whatsApp: '',
      tel: '',
      cargo: '',
      comissao: '',
      comissao_escrita: '',
      cep: '',
      logradouro: '',
      bairro: '',
      numero: '',
      municipio: '',
      estado: '',
      complemento: '',
      cnpj: '',
      razao_social: '',
      cnaeCode: '',
      cnaeText: ''
    },
    mode: 'onChange'
  })

  const nome = form.watch('nome')
  const comissao = form.watch('comissao')
  const cnpj = form.watch('cnpj')
  const cargo = form.watch('cargo')
  const registerCompany = form.watch('registerCompany')
  const hasSelectedCargo = Boolean(cargo)
  const hasSelectedCompanyOption = registerCompany === 'yes' || registerCompany === 'no'
  const shouldRegisterCompany = registerCompany === 'yes'

  useEffect(() => {
    if (!nome) return

    const words = nome.trim().split(/\s+/)
    const firstWord = words[0]?.toLowerCase() || ''
    const lastWord = words.length > 1 ? words[words.length - 1].toLowerCase() : ''
    const email = `${firstWord.replace(/[^a-z0-9]/g, '')}.${lastWord.replace(/[^a-z0-9]/g, '')}@${domain}`

    form.setValue('email', email, { shouldValidate: true })
  }, [nome, domain, form])

  useEffect(() => {
    if (!comissao) {
      form.setValue('comissao_escrita', '', { shouldValidate: false })
      return
    }

    const valorNumerico = Number(comissao)
    if (!Number.isNaN(valorNumerico) && valorNumerico >= 0 && valorNumerico <= 100) {
      form.setValue('comissao_escrita', numeroPorExtenso(valorNumerico), { shouldValidate: false })
    }
  }, [comissao, form])

  useEffect(() => {
    if (!shouldRegisterCompany) return
    if ((cnpj || '').length !== 18) return

    let ignore = false

    const loadCnpj = async () => {
      setIsCnpjLoading(true)
      try {
        const response = await buscarCNPJ(RemovaCharEspeciais(cnpj))
        if (!response || ignore) return

        const atividadePrincipal = response.atividade_principal?.[0] || {}

        form.setValue('razao_social', response.nome || '', { shouldValidate: true })
        form.setValue('bairro', response.bairro || '', { shouldValidate: true })
        form.setValue('numero', response.numero || '', { shouldValidate: true })
        form.setValue('municipio', response.municipio || '', { shouldValidate: true })
        form.setValue('estado', response.uf || '', { shouldValidate: true })
        form.setValue('complemento', response.complemento || '', { shouldValidate: false })
        form.setValue('logradouro', response.logradouro || '', { shouldValidate: true })
        form.setValue('cep', response.cep || '', { shouldValidate: true })
        form.setValue('cnaeCode', atividadePrincipal.code || '', { shouldValidate: true })
        form.setValue('cnaeText', atividadePrincipal.text || '', { shouldValidate: true })
      } catch {
        if (ignore) return
        setMessage({ type: 'error', text: 'Não foi possível consultar o CNPJ informado.' })
        setIsDialogOpen(true)
      } finally {
        if (!ignore) setIsCnpjLoading(false)
      }
    }

    loadCnpj()

    return () => {
      ignore = true
    }
  }, [buscarCNPJ, cnpj, form, shouldRegisterCompany])

  const handleCepChange = async (value: string) => {
    form.setValue('cep', value, { shouldValidate: true })
    const cleanCep = RemovaCharEspeciais(value)

    if (cleanCep.length !== 8) return

    setIsCepLoading(true)
    try {
      const response = await buscarCEP(cleanCep)
      if (!response) throw new Error('Nenhuma informação encontrada para este CEP.')

      form.setValue('bairro', response.bairro || '', { shouldValidate: true })
      form.setValue('municipio', response.localidade || response.municipio || '', { shouldValidate: true })
      form.setValue('estado', response.uf || '', { shouldValidate: true })
      form.setValue('logradouro', response.logradouro || '', { shouldValidate: true })
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Erro ao buscar CEP.'
      })
      setIsDialogOpen(true)
    } finally {
      setIsCepLoading(false)
    }
  }

  const handleEnviarMinuta = (email: string) => {
    createAgreement(
      {
        contracts: [
          shouldRegisterCompany ? TypeContratoServico.PARCERIA_COMERCIAL_CNPJ : TypeContratoServico.PARCERIA_COMERCIAL_CPF
        ],
        email
      },
      {
        onSuccess: () => {
          setMessage({
            type: 'success',
            text: 'O usuário e a minuta foram criados com sucesso!'
          })
          setIsDialogOpen(true)
        },
        onError: () => {
          setMessage({
            type: 'error',
            text: 'Houve um erro ao enviar a minuta.'
          })
          setIsDialogOpen(true)
        }
      }
    )
  }

  const onSubmit = async (data: FormValues) => {
    const info_user = {
      name: data.nome,
      cpf: RemovaCharEspeciais(data.cpf),
      email: data.email,
      password: data.password,
      whatsApp: data.whatsApp,
      tel: data.tel || '',
      typeCargo: data.cargo,
      cnpj: shouldRegisterCompany ? RemovaCharEspeciais(data.cnpj || '') : undefined,
      razao_social: shouldRegisterCompany ? data.razao_social : undefined,
      cnae: shouldRegisterCompany ? data.cnaeCode : undefined,
      atividadePrincipal: shouldRegisterCompany ? data.cnaeText : undefined,
      cep: data.cep,
      logradouro: data.logradouro,
      bairro: data.bairro,
      numero: data.numero,
      municipio: data.municipio,
      estado: data.estado,
      complemento: data.complemento
    }

    const regimeProduto = {
      comissao: Number(data.comissao),
      comissao_escrita: data.comissao_escrita || numeroPorExtenso(Number(data.comissao))
    }

    createUsuario(
      { info_user, Regime_produto: regimeProduto },
      {
        onSuccess: () => handleEnviarMinuta(data.email),
        onError: (error: any) => {
          setMessage({
            type: 'error',
            text: `Houve um erro ao criar o usuário: ${error.message || 'Erro desconhecido'}`
          })
          setIsDialogOpen(true)
        }
      }
    )
  }

  const resetStates = () => {
    form.reset()
    setMessage(null)
  }

  const availableRoles = getAvailableRoles(user?.typeCargo)

  return (
    <PageLayout
      title="Cadastrar Parceiro"
      footer={
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 w-full">
          <Button type="submit" form="createPartnerForm" className="w-full bg-primary hover:bg-primary-hover text-primary-foreground">
            Criar Usuário
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full bg-primary hover:bg-primary-hover text-primary-foreground"
            onClick={resetStates}
          >
            Novo Cadastro
          </Button>
        </div>
      }
    >
      <Form {...form}>
        <form id="createPartnerForm" onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="nome"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome Completo</FormLabel>
                  <FormControl>
                    <Input {...field} className="bg-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="cpf"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CPF</FormLabel>
                  <FormControl>
                    <CPFInput value={field.value} onCPFChange={value => form.setValue('cpf', value, { shouldValidate: true })} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>E-mail</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" className="bg-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="cargo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cargo</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="bg-input text-foreground">
                        <SelectValue placeholder="Selecione o cargo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableRoles.map(role => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {hasSelectedCargo && (
            <>
              <div className="space-y-4 rounded-lg border border-border p-4">
                <div className="text-sm font-medium text-muted-foreground">
                  Após definir o cargo, preencha os contatos, a comissão e escolha se deseja cadastrar a empresa.
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="whatsApp"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>WhatsApp</FormLabel>
                        <FormControl>
                          <WhatsAppInput
                            value={field.value}
                            onChange={value => form.setValue('whatsApp', value, { shouldValidate: true })}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="tel"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Telefone</FormLabel>
                        <FormControl>
                          <WhatsAppInput value={field.value || ''} onChange={field.onChange} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="comissao"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Comissão (%)</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="number"
                            min={0}
                            max={100}
                            className="bg-input"
                            onChange={event => {
                              field.onChange(event)
                              form.trigger('comissao')
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
             <FormField
                  control={form.control}
                  name="comissao_escrita"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Comissão por extenso</FormLabel>
                      <FormControl>
                        <Input {...field} readOnly disabled className="bg-input text-foreground" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
        
                </div>
          <FormField
                    control={form.control}
                    name="registerCompany"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Deseja cadastrar a empresa do usuário?</FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="bg-input text-foreground">
                              <SelectValue placeholder="Selecione uma opção" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="no">Não</SelectItem>
                            <SelectItem value="yes">Sim</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
   
              </div>

              {hasSelectedCompanyOption &&
                (shouldRegisterCompany ? (
                <div className="space-y-4 rounded-lg border border-border p-4">
                  <div className="text-sm font-medium text-muted-foreground">
                    Informe o CNPJ para preencher automaticamente os dados da empresa e do endereço.
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="cnpj"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>CNPJ</FormLabel>
                          <FormControl>
                            <CNPJInput value={field.value || ''} onChange={field.onChange} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="razao_social"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Razão Social</FormLabel>
                          <FormControl>
                            <Input {...field} className="bg-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="cnaeCode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>CNAE Code</FormLabel>
                          <FormControl>
                            <Input {...field} className="bg-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="cnaeText"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Atividade Principal</FormLabel>
                          <FormControl>
                            <Input {...field} className="bg-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {isCnpjLoading && <p className="text-sm text-muted-foreground">Consultando CNPJ...</p>}

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="cep"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>CEP</FormLabel>
                          <FormControl>
                            <CEPInput value={field.value || ''} onChange={handleCepChange} loading={isCepLoading} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="numero"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Número</FormLabel>
                          <FormControl>
                            <Input {...field} className="bg-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="logradouro"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Logradouro</FormLabel>
                          <FormControl>
                            <Input {...field} className="bg-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="bairro"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Bairro</FormLabel>
                          <FormControl>
                            <Input {...field} className="bg-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="municipio"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Município</FormLabel>
                          <FormControl>
                            <Input {...field} className="bg-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="estado"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>UF</FormLabel>
                          <FormControl>
                            <Input {...field} className="bg-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="complemento"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Complemento</FormLabel>
                        <FormControl>
                          <Input {...field} className="bg-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              ) : (
                <div className="space-y-4 rounded-lg border border-border p-4">
                  <div className="text-sm font-medium text-muted-foreground">
                    Como não haverá empresa vinculada, informe o CEP e o endereço do usuário.
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="cep"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>CEP</FormLabel>
                          <FormControl>
                            <CEPInput value={field.value || ''} onChange={handleCepChange} loading={isCepLoading} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="numero"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Número</FormLabel>
                          <FormControl>
                            <Input {...field} className="bg-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="logradouro"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Logradouro</FormLabel>
                          <FormControl>
                            <Input {...field} className="bg-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="bairro"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Bairro</FormLabel>
                          <FormControl>
                            <Input {...field} className="bg-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="municipio"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Município</FormLabel>
                          <FormControl>
                            <Input {...field} className="bg-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="estado"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>UF</FormLabel>
                          <FormControl>
                            <Input {...field} className="bg-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="complemento"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Complemento</FormLabel>
                        <FormControl>
                          <Input {...field} className="bg-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              ))}

              {hasSelectedCompanyOption && (
                <div className="space-y-4 rounded-lg border border-border p-4">
                  <PasswordConfirmationFields
                    form={form}
                    passwordName="password"
                    confirmationName="confirmPassword"
                    passwordLabel="Senha"
                    passwordPlaceholder="Digite uma senha forte"
                    confirmationLabel="Confirme a senha"
                    confirmationPlaceholder="Digite novamente a senha"
                  />
                </div>
              )}
            </>
          )}
        </form>
      </Form>

      <FeedbackDialog isOpen={isDialogOpen} onOpenChange={setIsDialogOpen} message={message} />
    </PageLayout>
  )
}
